import { useState, useRef, useEffect } from 'react';
import { Activity, Github, Upload, X, Sun, Moon, Monitor, Plus, HelpCircle, GitCompare, Check } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { useTheme } from '@/hooks/useTheme';
import { ShareButton } from '@/components/ShareButton';
import { AIInsightsButton } from '@/components/AIInsights';
import { ViewSwitcher } from '@/components/ViewSwitcher';
import { NetworkStatusIcon } from '@/components/NetworkStatusIcon';
import { cn } from '@/lib/utils';

export function Header() {
  const { har, tabs, activeTabId, setActiveTab, closeTab, addTab, loadCompareHAR, compareHar, compareFileName, clearCompareHAR } = useHARStore();
  const { theme, effectiveTheme, setTheme } = useTheme();
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setThemeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileLoad = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const content = await file.text();
      addTab(content, file.name);
    }
    // Reset input so same file can be loaded again
    e.target.value = '';
  };

  const handleCompareFileLoad = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const content = await file.text();
      loadCompareHAR(content, file.name);
    }
    // Reset input so same file can be loaded again
    e.target.value = '';
  };

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];

  const CurrentThemeIcon = theme === 'system' ? Monitor : (effectiveTheme === 'dark' ? Moon : Sun);

  return (
    <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-950/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-[1800px] mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-cyan-500 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg hidden sm:inline">
              <span className="text-gradient">HAR</span>
              <span className="text-slate-400">Insight</span>
            </span>
            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded font-mono" title="Deployed commit">
              {import.meta.env.VITE_GIT_SHA?.slice(0, 7) || 'dev'}
            </span>
          </div>

          {/* Tabs */}
          {tabs.length > 0 && (
            <div className="flex items-center gap-1 ml-2 sm:ml-4 overflow-x-auto scrollbar-none max-w-[calc(100vw-200px)] sm:max-w-none">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg cursor-pointer transition-colors max-w-[120px] sm:max-w-[180px] flex-shrink-0',
                    tab.id === activeTabId
                      ? 'bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300'
                      : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'
                  )}
                >
                  <span className="text-xs sm:text-sm truncate" title={tab.fileName}>
                    {tab.fileName}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeTab(tab.id);
                    }}
                    className={cn(
                      'p-0.5 rounded transition-colors flex-shrink-0',
                      tab.id === activeTabId
                        ? 'hover:bg-brand-200 dark:hover:bg-brand-500/30'
                        : 'hover:bg-slate-300 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100'
                    )}
                    title="Close tab"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {/* Add tab button */}
              <label
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800/50 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer transition-colors flex-shrink-0"
                title="Open another HAR file"
              >
                <Plus className="w-4 h-4" />
                <input
                  type="file"
                  accept=".har,.json"
                  className="hidden"
                  onChange={handleFileLoad}
                />
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {!har && (
            <label className="btn-ghost cursor-pointer flex items-center gap-2 p-1.5 sm:px-3 sm:py-2">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline text-xs sm:text-sm">Load HAR</span>
              <input
                type="file"
                accept=".har,.json"
                className="hidden"
                onChange={handleFileLoad}
              />
            </label>
          )}

          <div className="hidden sm:block">
            <AIInsightsButton />
          </div>

          {/* Compare Button - only show when HAR is loaded */}
          {har && (
            <div className="flex items-center gap-1">
              {!compareHar ? (
                <label
                  className="btn-ghost cursor-pointer flex items-center gap-1 sm:gap-2 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-500/10 p-1.5 sm:px-3 sm:py-2"
                  title="Compare with another HAR file"
                >
                  <GitCompare className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs sm:text-sm">Compare</span>
                  <input
                    type="file"
                    accept=".har,.json"
                    className="hidden"
                    onChange={handleCompareFileLoad}
                  />
                </label>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300 rounded-lg max-w-[100px] sm:max-w-[180px]">
                  <GitCompare className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate" title={compareFileName || ''}>
                    {compareFileName}
                  </span>
                  <button
                    onClick={clearCompareHAR}
                    className="p-0.5 hover:bg-brand-200 dark:hover:bg-brand-500/30 rounded transition-colors flex-shrink-0"
                    title="Clear comparison"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          <ViewSwitcher />

          <div className="hidden sm:block">
            <ShareButton />
          </div>

          <NetworkStatusIcon />

          {/* Theme Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
              className="btn-ghost p-1.5 sm:p-2 flex items-center gap-1"
              title={`Theme: ${theme === 'system' ? 'System (' + effectiveTheme + ')' : theme}`}
            >
              <CurrentThemeIcon className="w-5 h-5" />
              {theme === 'system' && (
                <span className="text-[10px] text-slate-400 hidden sm:inline">Auto</span>
              )}
            </button>

            {themeDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50">
                {themeOptions.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setTheme(value);
                      setThemeDropdownOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                      theme === value
                        ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1">{label}</span>
                    {theme === value && <Check className="w-4 h-4 text-brand-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => useHARStore.getState().setDocumentationOpen(true)}
            className="btn-ghost p-1.5 sm:p-2"
            title="How to use"
          >
            <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <a
            href="https://github.com/voioo/har-insight"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost p-1.5 sm:p-2 hidden sm:flex"
            title="View on GitHub"
          >
            <Github className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>
      </div>
    </header>
  );
}
